import {GROWTH_PACKAGES, SOCIAL_ADS_5_CODES} from '../../../utils/constants'

function findPreviewItem(preview, productCode) {
  return (
    preview?.centers?.find(({planCode}) => planCode === productCode) ??
    preview?.apps?.find(
      ({planCode, addonCode}) =>
        addonCode === productCode || (planCode === productCode && !addonCode),
    ) ??
    preview?.oneTimes?.find(({planCode}) => planCode === productCode) ??
    null
  )
}

// For AU the response's `total` is used (subtotal - discount + tax).
// For all other countries, `subtotal - discount` is used to exclude tax from
// the due-today display; falls back to `total` when `subtotal` is absent.
function previewAmount(previewItem, isAU) {
  if (!previewItem) return null
  if (isAU) return Number(previewItem.total ?? 0)
  const subtotal = Number(previewItem.subtotal ?? previewItem.total ?? 0)
  const discount = Number(previewItem.discount ?? 0)
  return Math.max(subtotal - discount, 0)
}

// Growth packages (see GROWTH_PACKAGES) and Social Ads plans (see
// SOCIAL_ADS_5_CODES) are sent to preview nested as addons under
// 'appmarket-5-thryv-m2m' (see buildPaymentPreviewPayload), and the backend
// echoes each one back as its own row: {planCode: 'appmarket-5-thryv-m2m',
// addonCode: <growth package or social ads code>, total}. Pull those rows
// out by addonCode so each one's own amount is applied directly to its item,
// rather than folding into appmarket-5-thryv-m2m's own amount — the two must
// not double count the same dollars.
const NESTED_MARKETPLACE_ADDON_CODES = [
  ...GROWTH_PACKAGES,
  ...SOCIAL_ADS_5_CODES,
]

function findNestedGrowthPackageAmounts(preview, isAU) {
  const amounts = new Map()
  ;(preview?.apps ?? []).forEach(row => {
    if (
      row.planCode === 'appmarket-5-thryv-m2m' &&
      NESTED_MARKETPLACE_ADDON_CODES.includes(row.addonCode)
    ) {
      amounts.set(row.addonCode, previewAmount(row, isAU))
    }
  })
  return amounts
}

// Maps a `cc/order/preview-payment` response onto a normalized item list.
// Only dueTodayAmount is refreshed here — it's fetched live from Recurly and
// can change day to day (proration, trial conversion, etc). recurringAmount
// (the monthly total) is sent by the backend on save and must never be
// overwritten by this preview. Items missing from the Recurly response are
// set to dueTodayAmount: 0 — Recurly didn't charge them, meaning the client
// already owns them.
//
// Nested addons (e.g. SEO PowerBoost/Keywords under seobase-5-thryv-m2m) are
// returned by Recurly as flat entries in preview.apps with their own planCode
// and total. They must be matched individually and included in dueTodayTotal —
// otherwise their charges are dropped from the due-today display.
//
// `country` controls whether per-line tax is included in dueTodayAmount:
// AU uses `total` (tax-inclusive); all other countries use `subtotal` (pre-tax).
export function applyPaymentPreview(items, preview, country) {
  const isAU = country === 'AU'
  const nestedGrowthAmounts = findNestedGrowthPackageAmounts(preview, isAU)

  const updatedItems = items.map(item => {
    if (item.alreadyIncluded) {
      // Still process nested addons — they may be upgrades (alreadyIncluded: false
      // on the addon itself, e.g. SEO PowerBoost/Keywords quantity increase).
      const updatedAddons = (item.addons ?? []).map(addon => {
        if (addon.alreadyIncluded) return {...addon, dueTodayAmount: 0}
        const addonCode = addon.productCode ?? addon.code
        const addonAmount = previewAmount(
          findPreviewItem(preview, addonCode),
          isAU,
        )
        return {...addon, dueTodayAmount: addonAmount ?? 0}
      })
      return {
        ...item,
        dueTodayAmount: 0,
        ...(item.addons?.length && {addons: updatedAddons}),
      }
    }

    if (nestedGrowthAmounts.has(item.productCode)) {
      return {
        ...item,
        dueTodayAmount: nestedGrowthAmounts.get(item.productCode) ?? 0,
      }
    }

    if (
      item.productCode === 'appmarket-5-thryv-m2m' &&
      nestedGrowthAmounts.size
    ) {
      const ownRow = (preview?.apps ?? []).find(
        row =>
          row.planCode === 'appmarket-5-thryv-m2m' &&
          !NESTED_MARKETPLACE_ADDON_CODES.includes(row.addonCode),
      )
      return {...item, dueTodayAmount: previewAmount(ownRow, isAU) ?? 0}
    }

    const amount = previewAmount(
      findPreviewItem(preview, item.productCode),
      isAU,
    )

    const updatedAddons = (item.addons ?? []).map(addon => {
      if (addon.alreadyIncluded) return {...addon, dueTodayAmount: 0}
      const addonCode = addon.productCode ?? addon.code
      const addonAmount = previewAmount(
        findPreviewItem(preview, addonCode),
        isAU,
      )
      return {...addon, dueTodayAmount: addonAmount ?? 0}
    })

    return {
      ...item,
      dueTodayAmount: amount ?? 0,
      ...(item.addons?.length && {addons: updatedAddons}),
    }
  })

  const dueTodayTotal = updatedItems.reduce(
    (sum, item) =>
      sum +
      Number(item.dueTodayAmount ?? 0) +
      (item.addons ?? []).reduce(
        (aSum, addon) => aSum + Number(addon.dueTodayAmount ?? 0),
        0,
      ),
    0,
  )
  return {items: updatedItems, dueTodayTotal}
}
